/**
 * Multi-reference helpers for Image Studio (uploads + @-mentions).
 */

import { expandMentionsInPrompt } from "../videoStudio/mentionAssets";

export const MAX_IMAGE_STUDIO_REFS = 6;

/**
 * @typedef {{
 *   id: string;
 *   previewUrl: string;
 *   remoteUrl?: string;
 *   file?: File;
 *   label: string;
 *   name?: string;
 * }} ImageStudioRef
 */

/**
 * @param {ImageStudioRef[]} refs
 */
export function collectStudioMentionAssets(refs = []) {
  return (refs || [])
    .filter((r) => r?.previewUrl || r?.remoteUrl)
    .map((r, index) => {
      const label = String(r.label || `Ref ${index + 1}`).trim() || `Ref ${index + 1}`;
      return {
        key: r.id || `studio:${index}`,
        token: `@[${label}](img:studio:${index})`,
        label,
        kind: "image",
        bucket: "studio",
        index,
        previewUrl: r.previewUrl || r.remoteUrl || "",
        name: r.name || label,
        color: "image",
      };
    });
}

/** Human-readable prompt text (@Label instead of raw tokens). */
export function promptWithVisibleMentions(text) {
  return String(text || "").replace(
    /@\[([^\]]+)\]\((img|vid):[^)]+\)/g,
    "@$1",
  );
}

/**
 * Expand @-mentions + reinforce that attached refs must drive the result.
 * @param {string} prompt
 * @param {ImageStudioRef[]} refs
 */
export function buildImageStudioApiPrompt(prompt, refs = []) {
  const assets = collectStudioMentionAssets(refs);
  let text = expandMentionsInPrompt(String(prompt || "").trim(), assets);
  if (!refs.length) return text;
  if (!/reference image/i.test(text)) {
    text = `${text}\n\nUse the attached reference image${refs.length > 1 ? "s" : ""} as the exact subject identity — match face, body, clothing, and details. Do not invent a different character.`;
  }
  return text;
}

export function labelFromFilename(name, index = 0) {
  const stem = String(name || "")
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[_-]+/g, " ")
    .trim();
  if (stem && stem.length <= 28 && !/^upload/i.test(stem)) return stem;
  return `Ref ${index + 1}`;
}

/** Convert a File to a durable data URL for chat thumbnails (survives revokeObjectURL). */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read image"));
    reader.readAsDataURL(file);
  });
}

/**
 * Prefer remote URLs; fall back to reading local Files as data URLs.
 * Blob URLs are not durable — never store them on chat turns.
 * @param {ImageStudioRef[]} refs
 * @returns {Promise<string[]>}
 */
export async function durableReferencePreviews(refs = []) {
  const out = [];
  for (const r of refs || []) {
    if (r?.remoteUrl && /^https?:\/\//i.test(r.remoteUrl)) {
      out.push(r.remoteUrl);
      continue;
    }
    if (r?.file) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await fileToDataUrl(r.file);
        if (dataUrl) out.push(dataUrl);
        continue;
      } catch {
        /* fall through */
      }
    }
    const preview = String(r?.previewUrl || "");
    if (preview && !preview.startsWith("blob:")) out.push(preview);
  }
  return out;
}
